import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Briefcase, BarChart3, GraduationCap, Users,
  MoreHorizontal, Layers, Timer, CalendarDays, Trophy, User, ClipboardCheck,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

// Four primary tabs always fit a 360px phone; everything else lives in "More".
const primaryItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Home" },
  { path: "/learn", icon: GraduationCap, label: "Learn" },
  { path: "/practice", icon: BookOpen, label: "Practice" },
  { path: "/progress", icon: BarChart3, label: "Stats" },
];

const moreItems = [
  { path: "/cases", icon: Briefcase, label: "Clinical cases", sub: "Scenario walk-throughs and role-play" },
  { path: "/trials", icon: Timer, label: "Readiness trials", sub: "Full-length timed exams" },
  { path: "/flashcards", icon: Layers, label: "Flashcards", sub: "Spaced-repetition recall" },
  { path: "/review", icon: ClipboardCheck, label: "Review mistakes", sub: "Questions you got wrong" },
  { path: "/study-plan", icon: CalendarDays, label: "Study plan", sub: "Your week-by-week schedule" },
  { path: "/community", icon: Users, label: "Community", sub: "Tips and wins from candidates" },
  { path: "/achievements", icon: Trophy, label: "Achievements", sub: "Streaks and milestones" },
  { path: "/account", icon: User, label: "Account & billing", sub: "Profile, plan, payments" },
];

const isActivePath = (pathname, path) =>
  pathname === path || (path !== "/dashboard" && pathname.startsWith(path));

const MobileNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = moreItems.some((i) => isActivePath(pathname, i.path));

  const go = (path) => { setMoreOpen(false); navigate(path); };

  const Tab = ({ icon: Icon, label, active, onClick, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`mobile-nav-item flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 h-full rounded-xl transition-colors
        ${active ? "active text-primary" : "text-muted-foreground"}`}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span className="text-[11px] leading-none truncate max-w-full">{label}</span>
    </button>
  );

  return (
    <>
      <nav
        className="mobile-nav fixed bottom-3 left-3 right-3 bg-card/95 backdrop-blur border border-border rounded-2xl h-16 flex items-stretch px-1 z-50 md:hidden shadow-lg"
        data-testid="mobile-nav"
      >
        {primaryItems.map((item) => (
          <Tab
            key={item.path}
            icon={item.icon}
            label={item.label}
            active={isActivePath(pathname, item.path)}
            onClick={() => go(item.path)}
            testid={`mobile-nav-${item.label.toLowerCase()}`}
          />
        ))}
        <Tab icon={MoreHorizontal} label="More" active={moreActive || moreOpen} onClick={() => setMoreOpen(true)} testid="mobile-nav-more" />
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl px-4 pb-8 pt-4 max-h-[85vh] overflow-y-auto md:hidden" data-testid="mobile-nav-more-sheet">
          <SheetHeader className="text-left mb-2">
            <SheetTitle className="text-base">Everything else</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-2">
            {moreItems.map((item) => {
              const active = isActivePath(pathname, item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => go(item.path)}
                  data-testid={`mobile-more-${item.path.slice(1)}`}
                  className={`text-left rounded-2xl border p-3 flex flex-col gap-1.5 transition-colors active:scale-[0.98]
                    ${active ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
                >
                  <item.icon className={`w-5 h-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  <span className="text-sm font-medium leading-tight">{item.label}</span>
                  <span className="text-[11px] text-muted-foreground leading-snug">{item.sub}</span>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileNav;
